import React, { useEffect, useMemo } from 'react';
import { useSubscriptionAnalyticsOverview } from '../../../hooks/api/useDashboard';
import { StatCard } from '../../molecules/StatCard';
import { RecentUsersList } from './RecentUsersList';
import type { AnalyticsDateRange, DataSource } from '../../../types/api/dashboard';

// ────────────────────────── Helpers ─────────────────────────────

function formatNumber(n: number): string {
  return n.toLocaleString();
}

// ────────────────────────── Section ───────────────────────────────

export interface SubscriptionsSectionProps {
  range: AnalyticsDateRange;
  /** Reports the worst-case data source (mock if any sub-hook is mock). */
  onSourceChange?: (source: DataSource) => void;
}

export const SubscriptionsSection: React.FC<SubscriptionsSectionProps> = ({
  range,
  onSourceChange,
}) => {
  const overview = useSubscriptionAnalyticsOverview(range);

  const aggregateSource: DataSource = useMemo(() => overview.dataSource, [overview.dataSource]);

  useEffect(() => {
    onSourceChange?.(aggregateSource);
  }, [aggregateSource, onSourceChange]);

  const overviewData = overview.data;

  const paymentDist = overviewData?.paymentStatusDistribution ?? {};
  const failedPayments = paymentDist.FAILED ?? 0;
  const completedPayments = paymentDist.COMPLETED ?? 0;
  const pendingPayments = paymentDist.PENDING ?? 0;

  const hasError = overview.error;

  return (
    <section className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Subscriptions</h3>
        <p className="text-xs text-gray-500">Counts for the selected date range</p>
      </div>

      {hasError ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          Failed to load subscription analytics.
        </div>
      ) : null}

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={
            <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
          }
          iconBgColor="bg-[#EAB308]"
          title="Active Subscriptions"
          value={overview.isLoading ? '…' : formatNumber(overviewData?.activeSubscriptions ?? 0)}
          trend={`${overviewData?.newSubscriptions ?? 0} new in range`}
          trendColor="text-gray-500"
        />
        <StatCard
          icon={
            <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                clipRule="evenodd"
              />
            </svg>
          }
          iconBgColor="bg-amber-500"
          title="Expired"
          value={overview.isLoading ? '…' : formatNumber(overviewData?.expiredSubscriptions ?? 0)}
          trend={`${overviewData?.upcomingExpirations ?? 0} upcoming expirations`}
          trendColor="text-gray-500"
        />
        <StatCard
          icon={
            <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          }
          iconBgColor="bg-orange-500"
          title="Cancelled"
          value={overview.isLoading ? '…' : formatNumber(overviewData?.cancelledSubscriptions ?? 0)}
          trend="in selected range"
          trendColor="text-gray-500"
        />
        <StatCard
          icon={
            <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.485 3.495c.673-1.16 2.357-1.16 3.03 0l6.28 10.875c.673 1.16-.165 2.625-1.515 2.625H3.72c-1.35 0-2.187-1.465-1.514-2.625L8.485 3.495zM10 6a1 1 0 011 1v3a1 1 0 11-2 0V7a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          }
          iconBgColor="bg-red-500"
          title="Failed Payments"
          value={overview.isLoading ? '…' : formatNumber(failedPayments)}
          trend={`${completedPayments} completed · ${pendingPayments} pending`}
          trendColor="text-gray-500"
        />
      </div>

      {/* Recent users list */}
      <RecentUsersList limit={10} />
    </section>
  );
};

export default SubscriptionsSection;
